import {useState} from 'react';
import type {SWRConfiguration} from 'swr';

import {HTTPStatus} from '../types';

import type {BaseClient, CacheKey, ExtractEndpointRequestType, ExtractEndpointResponseType} from './types';
import useSWRApi from './useSWRApi';

/**
 * Default configuration for polling
 */
const REFRESH_INTERVAL = 3000;
const REVALIDATE_ON_RECONNECT = true;
const REFRESH_WHEN_HIDDEN = true;
const RETRY_COUNT = 3;
const RETRY_INTERVAL = 5000;
const IGNORE_RETRY_STATUSES = [HTTPStatus.NOT_FOUND];

export const usePollingApi = <
  TClient extends BaseClient,
  TKey extends CacheKey<any, TClient>,
  TRequest extends ExtractEndpointRequestType<TClient, TKey[`endpoint`]>,
  TResponse extends ExtractEndpointResponseType<TClient, TKey[`endpoint`]>,
>(args: {
  client: TClient;
  key: TKey;
  request: TRequest;
  config?: SWRConfiguration<TResponse, any>;
}) => {
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const {client, key, request, config = {}} = args;

  const resolvedConfig = {
    ...defaultConfig,
    ...config,
    isPaused: () => {
      const consumerIsPaused = config.isPaused?.();
      return consumerIsPaused || isPaused;
    },
    onSuccess: () => {
      setIsPaused(true);
    },
  };

  const {isLoading, data, ...response} = useSWRApi(client, key, request, resolvedConfig);

  const error = response?.error || data?.error;

  return {
    start: () => setIsPaused(false),
    stop: () => setIsPaused(true),
    status: data?.data?.result?.type,
    payload: data?.data?.result?.payload,
    isLoading,
    error,
  };
};

const defaultConfig = {
  refreshInterval: REFRESH_INTERVAL,
  refreshWhenHidden: REFRESH_WHEN_HIDDEN,
  revalidateOnReconnect: REVALIDATE_ON_RECONNECT,
  onErrorRetry: (error, _key, _config, revalidate, {retryCount}) => {
    if (IGNORE_RETRY_STATUSES.includes(error.status)) return;

    if (retryCount >= RETRY_COUNT) return;

    setTimeout(() => revalidate({retryCount}), RETRY_INTERVAL);
  },
} satisfies Partial<SWRConfiguration>;
